import React, { useState } from "react";
import { Link } from "react-router-dom";
import { PageHero, BelieveBlock, Eyebrow, TrajectoryBand } from "../components/site/kit";
import { NextSteps, Pager } from "../components/site/extras";
import { Reveal } from "../components/site/motion";
import { track } from "../lib/analytics";
import { CONTACT_EMAIL } from "../lib/content";
import { useAbout } from "../lib/aboutApi";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EXPERTISE = [
  "Business Strategy & Transformation",
  "Brand, Marketing & Digital",
  "Sales & Distribution",
  "Technology, Product & Automation",
  "Consumer & Market Research",
  "Finance & Commercial Modelling",
  "Creative & Communication",
  "Process, Systems & Organisation",
];

const HOW_STEPS = [
  { n: "01", title: "Diagnose the real constraint", desc: "We look beyond the initial brief to understand what is actually holding growth back." },
  { n: "02", title: "Bring the right experts together", desc: "We assemble the subject-matter expertise required by that specific problem, not a standard project team." },
  { n: "03", title: "Build the strategy around execution", desc: "Recommendations are shaped by commercial reality, capability, systems, economics, ownership and what the organisation can realistically implement." },
  { n: "04", title: "Stay through action", desc: "We work alongside leadership, internal teams and existing partners until the agreed priorities move into execution." },
];

const field = "font-body w-full border border-white/25 bg-transparent px-4 py-3.5 text-lg font-normal text-white outline-none transition-colors placeholder:text-white/40 focus:border-[#5f8f10]";
const labelCls = "font-body mb-2 block text-sm font-semibold uppercase tracking-[0.12em] text-white/80";

const DiscoveryForm = () => {
  const [form, setForm] = useState({ name: "", email: "", company: "", website: "", problem: "" });
  const [status, setStatus] = useState("idle");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.problem.trim()) return;
    setStatus("loading");
    track("discovery_call_submitted", { source_page: "/about" });
    try {
      const res = await fetch(`${API}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          company: [form.company, form.website].filter(Boolean).join(" · "),
          message: form.problem,
          source_page: "/about",
          service: "Discovery Call",
          referrer: typeof document !== "undefined" ? document.referrer : "",
        }),
      });
      if (!res.ok) throw new Error("bad status");
      setStatus("done");
    } catch (err) {
      setStatus("error");
    }
  };

  if (status === "done") {
    return (
      <div data-testid="discovery-confirm" className="border-2 border-white/20 p-8">
        <div className="inline-flex h-14 w-14 items-center justify-center bg-[#5f8f10] text-2xl font-bold text-[#161A15]">✓</div>
        <h3 className="font-display mt-6 text-2xl uppercase tracking-tight text-white md:text-3xl">Thank you.</h3>
        <p className="font-body mt-4 max-w-md text-lg font-normal leading-relaxed text-white/80">
          We&rsquo;ve received your details. A member of the SWITCH team will read your growth problem and be in touch to line up a focused 30-minute discovery conversation.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="discovery-form" className="space-y-5 border-2 border-white/20 p-7 md:p-8">
      <div>
        <label htmlFor="dc-name" className={labelCls}>Name</label>
        <input id="dc-name" required data-testid="discovery-name" className={field} value={form.name} onChange={set("name")} placeholder="Your full name" />
      </div>
      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <label htmlFor="dc-email" className={labelCls}>Work email</label>
          <input id="dc-email" required type="email" data-testid="discovery-email" className={field} value={form.email} onChange={set("email")} placeholder="name@company.com" />
        </div>
        <div>
          <label htmlFor="dc-company" className={labelCls}>Company</label>
          <input id="dc-company" data-testid="discovery-company" className={field} value={form.company} onChange={set("company")} placeholder="Company name" />
        </div>
      </div>
      <div>
        <label htmlFor="dc-website" className={labelCls}>Website</label>
        <input id="dc-website" data-testid="discovery-website" className={field} value={form.website} onChange={set("website")} placeholder="https://" />
      </div>
      <div>
        <label htmlFor="dc-problem" className={labelCls}>What growth problem are you trying to solve?</label>
        <textarea id="dc-problem" required rows={4} data-testid="discovery-problem" className={`${field} resize-none`} value={form.problem} onChange={set("problem")} placeholder="Tell us a little about the business, what is not moving and what you are trying to achieve." />
      </div>
      {status === "error" && (
        <p data-testid="discovery-error" className="font-body text-base font-semibold text-[#5f8f10]">Something went wrong. Please try again, or write to {CONTACT_EMAIL}.</p>
      )}
      <button type="submit" disabled={status === "loading"} data-testid="discovery-submit" className="font-body inline-flex w-full items-center justify-between gap-3 border-2 border-[#5f8f10] bg-[#5f8f10] px-8 py-4 text-sm font-semibold uppercase tracking-[0.14em] text-[#161A15] transition-colors hover:border-white hover:bg-white disabled:opacity-60">
        {status === "loading" ? "Sending…" : "Book a 30-Minute Discovery Call"} <span aria-hidden>→</span>
      </button>
      <p className="font-body text-sm font-normal text-white/55">A few details. The right experts. A better first conversation.</p>
    </form>
  );
};

export default function About() {
  const about = useAbout();
  const publishedLeaders = (about.leaders || []).filter((l) => l.published !== false && (l.name || "").trim());
  const leadershipTier = publishedLeaders.filter((l) => (l.tier || "leadership") === "leadership");
  const aboutLeaders = (leadershipTier.length ? leadershipTier : publishedLeaders).slice(0, 6);
  return (
    <div className="font-body bg-white">
      <PageHero
        testId="about-hero"
        eyebrow="About"
        crumbs={[{ label: "Home", href: "/" }, { label: "About" }]}
        title={<>A growth company, built to turn strategy into <span className="text-[#5f8f10]">execution.</span></>}
        subcopy="Most firms specialise in one discipline and hand you a plan. We connect the disciplines that affect growth, find the real constraint, and stay in the room until the business moves."
      />

      <BelieveBlock testId="about-believe" />

      {about.story && about.story.length > 0 && (
        <section data-testid="about-story" className="border-b border-black/10 bg-white">
          <div className="mx-auto grid max-w-[1360px] gap-12 px-6 py-24 md:grid-cols-12 md:px-12 md:py-28 lg:gap-16 lg:px-16">
            <div className="md:col-span-5">
              <Eyebrow>{about.storyEyebrow}</Eyebrow>
              <h2 className="font-display mt-6 text-4xl leading-[0.95] tracking-tight text-black md:text-5xl">{about.storyHeading}</h2>
            </div>
            <div className="md:col-span-7">
              <div className="max-w-2xl space-y-5">
                {about.story.map((p, i) => (
                  <p key={i} className="font-body text-lg font-normal leading-relaxed text-black/85">{p}</p>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {aboutLeaders.length > 0 && (
        <section data-testid="about-leadership" className="border-b border-white/10 bg-[#161A15]">
          <div className="mx-auto max-w-[1360px] px-6 py-24 md:px-12 md:py-28 lg:px-16">
            <Eyebrow dark>{about.leadersEyebrow}</Eyebrow>
            <h2 className="font-display mt-6 text-4xl leading-[0.95] tracking-tight text-white md:text-5xl">{about.leadersHeading}</h2>
            <div className="mt-12 grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
              {aboutLeaders.map((l, i) => (
                <Reveal key={i} delay={(i % 3) * 0.06} data-testid={`about-leader-${i}`} className="flex flex-col border border-white/12 bg-[#1b1f19] p-6">
                  {l.image ? (
                    <img src={l.image} alt={l.name} className="mb-5 aspect-square w-full object-cover" />
                  ) : (
                    <div aria-hidden className="mb-5 flex aspect-square w-full items-center justify-center bg-white/5 font-display text-5xl text-[#5f8f10]">{(l.name || "?").charAt(0)}</div>
                  )}
                  <h3 className="font-display text-xl uppercase leading-tight tracking-tight text-white">{l.name}</h3>
                  <p className="font-body mt-1 text-sm font-semibold uppercase tracking-[0.12em] text-[#5f8f10]">{l.role}</p>
                  {l.bio && <p className="font-body mt-4 text-base font-normal leading-relaxed text-white/70">{l.bio}</p>}
                </Reveal>
              ))}
            </div>
            <div className="mt-12">
              <Link to="/team" data-testid="about-team-cta" className="font-body inline-flex items-center gap-3 border-2 border-[#5f8f10] px-8 py-4 text-sm font-semibold uppercase tracking-[0.14em] text-white transition-colors duration-200 hover:bg-[#5f8f10] hover:text-[#161A15]">
                Meet the full team <span aria-hidden>→</span>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Fold 2: The Expertise Around the Problem */}
      <section data-testid="about-expertise" className="border-b border-white/10 bg-[#161A15]">
        <div className="mx-auto max-w-[1360px] px-6 py-24 md:px-12 md:py-32 lg:px-16">
          <div className="grid gap-12 lg:grid-cols-12 lg:gap-16">
            <div className="lg:col-span-5">
              <Eyebrow dark>The expertise around the problem</Eyebrow>
              <h2 className="font-display mt-6 text-4xl leading-[0.95] tracking-tight text-white md:text-5xl lg:text-6xl">
                Growth problems do not belong to <span className="text-[#5f8f10]">one discipline.</span>
              </h2>
              <div className="mt-8 max-w-md space-y-4">
                <p className="font-body text-lg font-normal leading-relaxed text-white/80">A revenue problem may begin in marketing and end in distribution. A brand problem may actually be a proposition problem. A scaling problem may sit in technology, process, organisation or economics.</p>
                <p className="font-body text-lg font-normal leading-relaxed text-white/80">Which is why SWITCH brings senior expertise together, not as separate service lines, but as different lenses brought together around one business problem.</p>
              </div>
              <p className="font-display mt-8 text-2xl uppercase leading-tight tracking-tight text-[#5f8f10] md:text-3xl">One problem. One agenda. The right expertise.</p>
            </div>
            <div className="lg:col-span-7">
              <div className="grid gap-px border border-white/12 bg-white/12 sm:grid-cols-2">
                {EXPERTISE.map((d, i) => (
                  <Reveal key={d} delay={(i % 2) * 0.06} className="flex items-center gap-4 bg-[#161A15] p-6">
                    <span className="font-display text-2xl leading-none text-[#5f8f10]">{String(i + 1).padStart(2, "0")}</span>
                    <span className="font-display text-lg uppercase leading-[1.05] tracking-tight text-white md:text-xl">{d}</span>
                  </Reveal>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Fold 3: Problem First. Industry Second. */}
      <section data-testid="about-problem-first" className="border-b border-black/10 bg-[#F7F6F2]">
        <div className="mx-auto grid max-w-[1360px] gap-12 px-6 py-24 md:grid-cols-12 md:px-12 md:py-32 lg:gap-16 lg:px-16">
          <div className="md:col-span-5">
            <Eyebrow>Problem first. Industry second.</Eyebrow>
            <h2 className="font-display mt-6 text-4xl leading-[0.95] tracking-tight text-black md:text-5xl lg:text-6xl">
              We are <span className="text-[#5f8f10]">industry-agnostic</span> by design.
            </h2>
          </div>
          <div className="md:col-span-7">
            <div className="max-w-2xl space-y-5">
              <p className="font-body text-lg font-normal leading-relaxed text-black/85">We do not begin by asking which service we can sell you. We begin by asking: what is stopping the business from moving?</p>
              <p className="font-body text-lg font-normal leading-relaxed text-black/85">That distinction matters. The same symptom can have completely different causes in different businesses. Slowing growth may come from proposition, pricing, channel, demand, capability, technology, execution, or several of them working against one another.</p>
              <p className="font-body text-lg font-normal leading-relaxed text-black/85">So we diagnose the problem first. Then we bring in the expertise the problem requires. This allows SWITCH to work across industries without forcing businesses into predetermined service models.</p>
            </div>
            <p className="font-display mt-8 border-t border-black/15 pt-8 text-2xl uppercase leading-tight tracking-tight text-black md:text-3xl">The problem determines the expertise. <span className="text-[#5f8f10]">Not the other way around.</span></p>
          </div>
        </div>
      </section>

      <TrajectoryBand src="/assets/editorial-about.jpg" testId="about-band" label="Many disciplines, one growth agenda" />

      {/* Fold 4: How We Work */}
      <section data-testid="about-how-we-work" className="border-b border-white/10 bg-[#161A15]">
        <div className="mx-auto max-w-[1360px] px-6 py-24 md:px-12 md:py-32 lg:px-16">
          <div className="max-w-3xl">
            <Eyebrow dark>How we work</Eyebrow>
            <h2 className="font-display mt-6 text-4xl leading-[0.95] tracking-tight text-white md:text-5xl lg:text-6xl">
              Diagnose. Build. <span className="text-[#5f8f10]">Execute.</span>
            </h2>
          </div>
          <div className="mt-14 border-t border-white/15">
            {HOW_STEPS.map((st, i) => (
              <Reveal key={st.n} delay={i * 0.1} className="group grid items-baseline gap-6 border-b border-white/12 py-10 md:grid-cols-12 md:gap-10">
                <div className="md:col-span-3">
                  <span className="font-display block text-6xl leading-none text-white/25 transition-colors duration-300 group-hover:text-[#5f8f10] md:text-7xl">{st.n}</span>
                </div>
                <div className="md:col-span-9">
                  <h3 className="font-display text-2xl uppercase leading-[1.02] tracking-tight text-[#5f8f10] md:text-3xl">{st.title}</h3>
                  <p className="font-body mt-4 max-w-2xl text-lg font-normal leading-relaxed text-white/70">{st.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <div className="mt-12 max-w-2xl">
            <p className="font-body text-sm font-semibold uppercase tracking-[0.14em] text-white/55">Why this works</p>
            <p className="font-body mt-4 text-xl font-normal leading-relaxed text-white/85">
              Because the people defining the direction understand what it will take to deliver it. Fewer handovers. Fewer disconnected recommendations. Fewer gaps between thinking and doing.
            </p>
            <p className="font-display mt-6 text-2xl uppercase leading-tight tracking-tight text-white md:text-3xl">One growth problem. <span className="text-[#5f8f10]">One integrated path from diagnosis to execution.</span></p>
          </div>
        </div>
      </section>

      {/* Fold 5: Start With a 30-Minute Discovery Call */}
      <section id="discovery" data-testid="about-discovery" className="border-t-2 border-[#5f8f10] bg-[#161A15]">
        <div className="mx-auto grid max-w-[1360px] gap-12 px-6 py-24 md:grid-cols-12 md:px-12 md:py-28 lg:gap-16 lg:px-16">
          <div className="md:col-span-5">
            <Eyebrow dark>Start with a 30-minute discovery call</Eyebrow>
            <h2 className="font-display mt-6 text-4xl leading-[0.95] tracking-tight text-white md:text-5xl lg:text-6xl">
              Bring us the <span className="text-[#5f8f10]">problem.</span>
            </h2>
            <div className="mt-8 max-w-md space-y-4">
              <p className="font-body text-lg font-normal leading-relaxed text-white/80">You do not need a finished brief. Tell us a little about the business, what is not moving and what you are trying to achieve.</p>
              <p className="font-body text-lg font-normal leading-relaxed text-white/80">That gives us enough context to understand the problem and line up the right subject-matter experts for a focused 30-minute discovery conversation.</p>
            </div>
          </div>
          <div className="md:col-span-7">
            <DiscoveryForm />
          </div>
        </div>
      </section>

      <NextSteps links={[
        { eyebrow: "How we work", title: "The SWITCH model", blurb: "The four moves that turn a growth problem into growth.", href: "/how-we-work" },
        { eyebrow: "What we solve", title: "Four ways growth gets stuck", blurb: "Enter by the growth problem in front of you.", href: "/what-we-solve" },
        { eyebrow: "Careers", title: "Work with us", blurb: "Problems that do not fit neatly into one discipline.", href: "/careers" },
      ]} />

      <Pager current="/about" />
    </div>
  );
}
